import React, { useState, useEffect, useCallback } from 'react';
import MPDashboardLayout from '../../components/mp/MPDashboardLayout';
import { auditApi } from '../../lib/mpApi';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Shield, Loader2, Clock, User, Search, ChevronDown, ChevronRight,
  PlusCircle, Pencil, Trash2, Send, Eye, Power, LogIn, Settings, FileText,
} from 'lucide-react';

const actionIcons = {
  create: PlusCircle,
  update: Pencil,
  delete: Trash2,
  publish: Send,
  enable: Power,
  disable: Power,
  login: LogIn,
  view: Eye,
};

const actionColors = {
  create: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  update: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  delete: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
  publish: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  enable: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  disable: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20' },
  login: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatFullDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Convert raw audit log to human-readable description
const describeAction = (log) => {
  const action = log.action?.toLowerCase() || 'unknown';
  const entity = log.entity_type || 'item';
  const user = log.user_name || 'Someone';
  const changes = log.changes || {};

  switch (action) {
    case 'create':
      if (changes.name) return `${user} created ${entity} "${changes.name}"`;
      if (changes.platform) return `${user} created a ${changes.platform} ${entity}`;
      return `${user} created a new ${entity}`;
    case 'update':
      const fields = Object.keys(changes).filter(k => k !== 'updated_at');
      if (fields.length === 0) return `${user} updated a ${entity}`;
      if (fields.length <= 3) return `${user} updated ${fields.join(', ')} on a ${entity}`;
      return `${user} updated ${fields.length} fields on a ${entity}`;
    case 'delete':
      if (changes.name) return `${user} deleted ${entity} "${changes.name}"`;
      return `${user} deleted a ${entity}`;
    case 'publish':
      return `${user} published a ${entity}`;
    case 'enable':
      return `${user} enabled a ${entity}`;
    case 'disable':
      return `${user} disabled a ${entity}`;
    case 'login':
      return `${user} logged in`;
    default:
      return `${user} performed "${action}" on ${entity}`;
  }
};

const formatChangeValue = (value) => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const AuditLogEntry = ({ log }) => {
  const [expanded, setExpanded] = useState(false);
  const action = log.action?.toLowerCase() || 'unknown';
  const matchedAction = Object.keys(actionColors).find(k => action.includes(k)) || 'update';
  const colors = actionColors[matchedAction];
  const Icon = actionIcons[matchedAction] || Settings;
  const changes = log.changes || {};
  const changeKeys = Object.keys(changes).filter(k => k !== 'updated_at' && k !== '_id');
  const hasChanges = changeKeys.length > 0;

  return (
    <div className="group bg-card border border-border hover:border-border rounded-lg transition-colors"
      data-testid={`mp-audit-${log.log_id}`}>
      <div className="flex items-start gap-3 p-4 cursor-pointer" onClick={() => hasChanges && setExpanded(!expanded)}>
        <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${colors.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-muted-foreground">{describeAction(log)}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge className={`text-[10px] border ${colors.bg} ${colors.text} ${colors.border}`}>{log.action}</Badge>
            <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">{log.entity_type}</Badge>
            {log.entity_id && <span className="text-[10px] text-muted-foreground font-mono">{log.entity_id}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            <p className="text-xs text-muted-foreground">{formatDateTime(log.created_at)}</p>
            <p className="text-[10px] text-muted-foreground">{formatFullDate(log.created_at)}</p>
          </div>
          {hasChanges && (
            <button className="text-muted-foreground hover:text-foreground/80 transition">
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>
      {expanded && hasChanges && (
        <div className="px-4 pb-4 pl-[52px]">
          <div className="bg-muted rounded-lg border border-border overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-3 py-2 text-muted-foreground font-medium uppercase tracking-wider">Field</th>
                  <th className="text-left px-3 py-2 text-muted-foreground font-medium uppercase tracking-wider">Value</th>
                </tr>
              </thead>
              <tbody>
                {changeKeys.map(key => (
                  <tr key={key} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 text-muted-foreground font-mono">{key}</td>
                    <td className="px-3 py-2 text-foreground/80 font-mono break-all max-w-[400px]">
                      <span className="line-clamp-2">{formatChangeValue(changes[key])}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const MPAuditPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterEntity, setFilterEntity] = useState('all');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filterAction !== 'all') filters.action = filterAction;
      if (filterEntity !== 'all') filters.entity_type = filterEntity;
      const data = await auditApi.getAll(200, filters);
      setLogs(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load audit logs'); }
    finally { setLoading(false); }
  }, [filterAction, filterEntity]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredLogs = logs.filter(log => {
    if (!search) return true;
    const s = search.toLowerCase();
    return log.user_name?.toLowerCase().includes(s) ||
      log.action?.toLowerCase().includes(s) ||
      log.entity_type?.toLowerCase().includes(s) ||
      log.entity_id?.toLowerCase().includes(s);
  });

  const uniqueActions = [...new Set(logs.map(l => l.action).filter(Boolean))];
  const uniqueEntities = [...new Set(logs.map(l => l.entity_type).filter(Boolean))];

  // Group by date
  const groupedByDate = {};
  filteredLogs.forEach(log => {
    const date = log.created_at ? new Date(log.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Unknown';
    if (!groupedByDate[date]) groupedByDate[date] = [];
    groupedByDate[date].push(log);
  });

  return (
    <MPDashboardLayout title="Audit Logs">
      <div className="space-y-6" data-testid="mp-audit-page">
        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search logs..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-muted border-border text-foreground placeholder:text-muted-foreground" data-testid="mp-audit-search" />
          </div>
          <Select value={filterAction} onValueChange={setFilterAction}>
            <SelectTrigger className="w-36 bg-muted border-border text-foreground">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {uniqueActions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterEntity} onValueChange={setFilterEntity}>
            <SelectTrigger className="w-36 bg-muted border-border text-foreground">
              <SelectValue placeholder="Entity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              {uniqueEntities.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-card border border-border rounded-lg px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Total Logs</p>
            <p className="text-2xl font-bold text-foreground">{logs.length}</p>
          </div>
          <div className="bg-card border border-border rounded-lg px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Creates</p>
            <p className="text-2xl font-bold text-emerald-400">{logs.filter(l => l.action?.includes('create')).length}</p>
          </div>
          <div className="bg-card border border-border rounded-lg px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Updates</p>
            <p className="text-2xl font-bold text-blue-400">{logs.filter(l => l.action?.includes('update')).length}</p>
          </div>
          <div className="bg-card border border-border rounded-lg px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Deletes</p>
            <p className="text-2xl font-bold text-red-400">{logs.filter(l => l.action?.includes('delete')).length}</p>
          </div>
        </div>

        {/* Logs grouped by date */}
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground"><Shield className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="text-sm">No audit logs found</p></div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedByDate).map(([date, dateLogs]) => (
              <div key={date}>
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                  <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{date}</h3>
                  <div className="flex-1 h-px bg-muted" />
                  <span className="text-[10px] text-muted-foreground">{dateLogs.length} events</span>
                </div>
                <div className="space-y-2">
                  {dateLogs.map(log => <AuditLogEntry key={log.log_id} log={log} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MPDashboardLayout>
  );
};

export default MPAuditPage;
